import type { CaptionsItem } from '../../vendor/src/features/remotion-editor/editor/items/captions/captions-item-type';
import type { EditorState } from '../../vendor/src/features/remotion-editor/editor/state/types';
import { relayoutCaptions } from '../../vendor/src/features/remotion-editor/editor/state/actions/relayout-captions';
import { hasPendingTimelineEdit } from './edit-ownership';

export type CaptionStyleScope = 'track' | 'all';
// Visual typography only: never copy words, source clocks, pagination timing or presentation links.
const STYLE_KEYS = [
  'fontFamily','fontStyle','fontSize','lineHeight','letterSpacing','align','direction',
  'color','colorVisible','highlightColor','highlightVisible','highlightMode','highlightEffect',
  'strokeWidth','strokeColor','strokeVisible','shadow','shadowVisible','background','backgroundVisible',
  'maxLines','sourceTypography',
] as const satisfies readonly (keyof CaptionsItem)[];

export function captionStyleTargets(state: EditorState, sourceId: string, scope: CaptionStyleScope) {
  const doc = state.undoableState;
  const source = doc.items[sourceId];
  const sourceTracks = doc.tracks.filter(t => t.items.includes(sourceId));
  if (source?.type !== 'captions' || sourceTracks.length !== 1)
    throw Error('타임라인에서 기준으로 삼을 자막 하나를 선택해 주세요.');
  const targetIds: string[] = [], lockedIds = new Set<string>();
  for (const track of doc.tracks) {
    if (scope === 'track' && track.id !== sourceTracks[0]!.id) continue;
    for (const id of track.items) {
      if (id === sourceId || doc.items[id]?.type !== 'captions') continue;
      // Ambiguous ownership is not safe to mutate, even if one occurrence is unlocked.
      if (doc.tracks.filter(t => t.items.includes(id)).length !== 1 || track.locked) lockedIds.add(id);
      else if (!targetIds.includes(id)) targetIds.push(id);
    }
  }
  return { source, targetIds: targetIds.filter(id => !lockedIds.has(id)), skipped: lockedIds.size };
}

export function applyCaptionStyle(state: EditorState, sourceId: string, scope: CaptionStyleScope, includeLayout = false) {
  if (hasPendingTimelineEdit(state)) throw Error('이동이나 자르기를 마친 뒤 다시 적용해 주세요.');
  const {source,targetIds,skipped} = captionStyleTargets(state,sourceId,scope);
  if (!targetIds.length) throw Error('적용할 다른 자막이 없습니다. 자막을 추가하거나 트랙 잠금을 해제해 주세요.');
  const items = {...state.undoableState.items};
  let changed = 0;
  for (const id of targetIds) {
    const original = items[id] as CaptionsItem;
    const next = {...original};
    for (const key of STYLE_KEYS) {
      // Remove absent optional styles too, so a prior shadow/background does not linger.
      delete (next as unknown as Record<string,unknown>)[key];
      if (source[key] !== undefined)
        (next as unknown as Record<string,unknown>)[key] = structuredClone(source[key]);
    }
    if (includeLayout) Object.assign(next,{left:source.left,top:source.top,width:source.width,rotation:source.rotation});
    const laidOut = relayoutCaptions(next);
    const keys = [...STYLE_KEYS,'height',...(includeLayout ? ['left','top','width','rotation'] as const : [])] as const;
    if (keys.some(key => JSON.stringify(laidOut[key]) !== JSON.stringify(original[key]))) {items[id]=laidOut;changed++;}
  }
  return {state: changed ? {...state,undoableState:{...state.undoableState,items}} : state, changed, skipped};
}
